import type { ManagerOptions, SocketOptions } from 'socket.io-client';
import { getAuthProvider } from '@/auth/auth-provider';
import { config } from '@/config';
import { isDevEnv } from '@/config/utils';
import type { SocketEvent, SocketNamespace } from '@/enums/websocket';

type Logger = (namespace: SocketNamespace) => (event: SocketEvent, data?: unknown) => void;

const logEvent: Logger = namespace => (event, data) => {
  const types = {
    connect: logInfo,
    disconnect: logWarn,
    authenticated: logInfo,
    unauthorized: logWarn,
  } as Record<string, Logger>;

  const logger: Logger = types[event] || logInfo;
  logger(namespace)(event, data);
};

const logInfo: Logger = namespace => (event, data) => {
  if (!isDevEnv()) return;

  console.groupCollapsed(
    `%c socket:${namespace} %c${event.toUpperCase()} %c⌁`,
    'color: #979797;',
    'color: #aaffff; font-weight: bold;',
    'color: #d7ff00;',
  );
  if (data) console.info(data);
  console.groupEnd();
};

const logWarn: Logger = namespace => (event, data) => {
  if (!isDevEnv()) return;

  console.groupCollapsed(
    `%c socket:${namespace} %c${event.toUpperCase()} %c⌁`,
    'color: #979797;',
    'color: #aaffff; font-weight: bold;',
    'color: #c10d34',
  );
  if (data) console.info(data);
  console.groupEnd();
};

const logGeneric = (namespace: SocketNamespace) => (value: string) => {
  console.log(
    `%c socket:${namespace} %c${value} %c⌁`,
    'color: #979797;',
    'color: #aaffff; font-weight: bold;',
    'color: #c10d34',
  );
};

export const createLogger = (namespace: SocketNamespace) => ({
  event: logEvent(namespace),
  log: logGeneric(namespace),
  info: logInfo(namespace),
  warn: logWarn(namespace),
});

export function resolveSocketOptions(opts: Partial<ManagerOptions & SocketOptions> = {}): Partial<ManagerOptions & SocketOptions> {
  return {
    path: config().BRAND_INSIGHTS_WS_PATH,
    withCredentials: true,
    auth: callback => {
      const $provider = getAuthProvider();
      if ($provider.authSource === 'jwt' && $provider.accessToken) {
        return callback({
          token: $provider.accessToken,
        });
      }
      return callback(undefined);
    },
    transports: ['websocket', 'polling'],
    ...opts,
  };
}